
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Event } from '@/lib/types';
import { Search, Calendar, MapPin, Users, CalendarIcon, ArrowDownAZ, ArrowUpAZ, Tag, Award, Clock, Briefcase } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { formatEventDate, getEventDateRangeString } from '@/lib/eventHelpers';
import { formatDistanceToNow, differenceInDays, isBefore, isToday, isTomorrow, isAfter, isWithinInterval } from 'date-fns';
import { hu } from 'date-fns/locale';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/use-mobile';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { useQuery } from '@tanstack/react-query';
import { loadProfessions } from '@/services/professionService';
import { Separator } from '@/components/ui/separator';
import StatusBadge from '@/components/admin/StatusBadge';
import { EventStatus } from '@/services/eventService';
import { eventTypeLabels } from '@/lib/eventLabels';
import { sendSearchEvent } from '@/services/analyticsService';

interface EventListViewProps {
  events: Event[];
  onEventClick: (event: Event) => void;
}

const EventListView: React.FC<EventListViewProps> = ({ events, onEventClick }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [sortBy, setSortBy] = useState<'date' | 'name' | 'location'>('date');
  const isMobile = useIsMobile();

  // Send search analytics event when search term changes
  useEffect(() => {
    if (searchTerm.length > 2) {
      const resultsCount = filteredEvents.length;
      sendSearchEvent(searchTerm, resultsCount);
    }
  }, [searchTerm]);

  const { data: professions = [] } = useQuery({
    queryKey: ['professions'],
    queryFn: loadProfessions,
  });

  const getProfessionName = (id: string): string => {
    const profession = professions.find(p => p.id === id);
    return profession ? profession.name : id;
  };

  const filteredEvents = events.filter(event =>
    event.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    event.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (event.venue?.city && event.venue.city.toLowerCase().includes(searchTerm.toLowerCase())) ||
    event.type.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const now = new Date();

  // Identify currently happening events
  const currentlyHappeningEvents = filteredEvents.filter(event => {
    const eventStart = new Date(event.dateStart);
    const eventEnd = new Date(event.dateEnd);
    return isWithinInterval(now, { start: eventStart, end: eventEnd });
  });

  // Upcoming events that haven't started yet
  const upcomingEvents = filteredEvents.filter(event => {
    const eventStart = new Date(event.dateStart);
    return isAfter(eventStart, now);
  });

  // Past events that have completely ended
  const pastEvents = filteredEvents.filter(event => {
    const eventEnd = new Date(event.dateEnd);
    return isBefore(eventEnd, now);
  });

  const sortEvents = (eventsToSort: Event[]) => {
    return [...eventsToSort].sort((a, b) => {
      if (sortBy === 'date') {
        return sortDirection === 'asc'
          ? new Date(a.dateStart).getTime() - new Date(b.dateStart).getTime()
          : new Date(b.dateStart).getTime() - new Date(a.dateStart).getTime();
      } else if (sortBy === 'name') {
        return sortDirection === 'asc'
          ? a.name.localeCompare(b.name)
          : b.name.localeCompare(a.name);
      } else if (sortBy === 'location') {
        const locationA = a.venue?.city || '';
        const locationB = b.venue?.city || '';
        return sortDirection === 'asc'
          ? locationA.localeCompare(locationB)
          : locationB.localeCompare(locationA);
      }
      return 0;
    });
  };

  const sortedCurrentlyHappeningEvents = sortEvents(currentlyHappeningEvents);
  const sortedUpcomingEvents = sortEvents(upcomingEvents);
  const sortedPastEvents = [...pastEvents].sort((a, b) =>
    new Date(b.dateStart).getTime() - new Date(a.dateStart).getTime()
  );

  const toggleSortDirection = () => {
    setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
  };

  const getEventDuration = (dateStart: string, dateEnd: string): number => {
    const startDate = new Date(dateStart);
    const endDate = new Date(dateEnd);
    return differenceInDays(endDate, startDate) + 1;
  };

  const handleEventClick = (event: Event, e: React.MouseEvent) => {
    e.preventDefault(); // Prevent the default navigation behavior
    onEventClick(event);
  };

  const renderEventCard = (event: Event) => {
    const eventDate = new Date(event.dateStart);
    const isUpcoming = !isBefore(eventDate, now);
    const eventDuration = getEventDuration(event.dateStart, event.dateEnd);
    const isMultiDay = eventDuration > 1;
    const dateRange = getEventDateRangeString(event.dateStart, event.dateEnd);

    // Check if the event is currently happening
    const isHappening = currentlyHappeningEvents.includes(event);

    return (
      <Card
        key={event.id}
        onClick={(e) => handleEventClick(event, e)}
        className={cn(
          "cursor-pointer group transition-all duration-300 ease-in-out",
          "hover:shadow-lg hover:border-accent/30",
          "transform hover:-translate-y-1 hover:scale-[1.01]",
          isHappening && "border-l-4 border-l-accent"
        )}
      >
        <div className="flex flex-col md:flex-row">
          {isMobile ? (
            <div className=" text-xl p-4  uppercase font-bold ">
              {dateRange}
            </div>

          ) : (
            <div className={cn(
              "flex-shrink-0 p-4 flex flex-col items-center justify-center w-full sm:w-24 text-center font-medium",
              isHappening ? "bg-accent/20" : isUpcoming ? "bg-muted/30" : "bg-gray-200"
            )}>
              <div className="text-sm uppercase ">{eventDate.toLocaleString('hu-HU', { month: 'short' }).toUpperCase()}</div>
              <div className="text-3xl font-bold">{eventDate.getDate()}</div>
              <div className="text-sm text-gray-400">{eventDate.getFullYear()}</div>
              {isMultiDay && (
                <div className="mt-2 text-xs flex items-center gap-1 bg-accent/10 text-accent rounded-full px-2 py-1 flex-nowrap text-nowrap">
                  <Clock className="h-3 w-3" />
                  {eventDuration} nap
                </div>
              )}
            </div>
          )}

          <div className="p-4 pt-0 md:p-4 flex-1 overflow-hidden">
            <div className="flex flex-col md:flex-row justify-between items-start">
              <div className="flex-1 min-w-0">
                <h3 className="text-lg font-semibold mb-1 ">{event.name}</h3>
                {event.description && <p className="text-sm text-muted-foreground mb-3 line-clamp-3 sm:line-clamp-2">{event.description}</p>}
              </div>

              <div className="flex md:flex-col gap-1  mb-4">
                <Badge
                  variant="secondary"
                  className="flex-shrink-0 bg-gray-100 text-gray-700 border border-gray-200"
                >
                  {eventTypeLabels[event.type as keyof typeof eventTypeLabels] || event.type}
                </Badge>
                {event.status && <StatusBadge
                  status={event.status as EventStatus}
                  showIcon={false}
                  className="text-xs"
                />}
              </div>
            </div>

            <div className="flex flex-wrap gap-x-4 gap-y-2 text-sm text-muted-foreground">
              {event.organizer?.name && (
                <div className="flex items-center max-w-[180px] sm:max-w-none">
                  <Briefcase className="h-4 w-4 mr-1 flex-shrink-0" />
                  <span className="truncate">{event.organizer.name}</span>
                </div>
              )}

              <div className="flex items-center">
                <Calendar className="h-4 w-4 mr-1 flex-shrink-0" />
                <span>{formatEventDate(event.dateStart)}</span>
              </div>

              {event.venue && (
                <div className="flex items-center max-w-[180px] sm:max-w-none">
                  <MapPin className="h-4 w-4 mr-1 flex-shrink-0" />
                  <span className="truncate">
                    {event.venue.online ? 'Online' :
                      `${event.venue.city}${event.venue.name ? `, ${event.venue.name}` : ''}`
                    }
                  </span>
                </div>
              )}

              {event.maxAttendees && (
                <div className="flex items-center">
                  <Users className="h-4 w-4 mr-1 flex-shrink-0" />
                  <span>Tervezett létszám: {event.maxAttendees} fő</span>
                </div>
              )}

              {event.professions && event.professions.length > 0 && (
                <div className="flex items-center max-w-[180px] sm:max-w-none">
                  <Tag className="h-4 w-4 mr-1 flex-shrink-0" />
                  <span className="truncate">
                    {event.professions.slice(0, 2).map(id => getProfessionName(id)).join(', ')}
                  </span>
                </div>
              )}
            </div>

            <div className="mt-3 flex flex-wrap gap-2">
              {isHappening && (
                <Badge variant="outline" className="text-xs bg-accent/20 text-accent border-accent/50 font-medium">
                  Jelenleg zajlik
                </Badge>
              )}

              {!isHappening && isUpcoming && (
                <Badge variant="outline" className="text-xs bg-accent/10 text-accent">
                  {isToday(eventDate) ? 'Ma' :
                    isTomorrow(eventDate) ? 'Holnap' :
                      formatDistanceToNow(eventDate, { addSuffix: true, locale: hu })}
                </Badge>
              )}

              {event.creditPoints && event.creditPoints > 0 && (
                <Badge variant="outline" className="text-xs bg-primary/10 text-primary flex items-center gap-1">
                  <Award className="h-3 w-3" />
                  {event.creditPoints} kreditpont
                </Badge>
              )}

              {event.isRecommended && (
                <Badge variant="outline" className="text-xs bg-accent/10 text-accent">
                  Ajánlott
                </Badge>
              )}
            </div>
          </div>
        </div>
      </Card>
    );
  };

  return (
    <div className="w-full space-y-4">
      <div className="flex flex-col sm:flex-row gap-2 justify-between mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Keresés eseményekben..."
            className="pl-8"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="flex gap-2">
          {!isMobile ? (
            <>
              <Select value={sortBy} onValueChange={(value) => setSortBy(value as 'date' | 'name' | 'location')}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Rendezés szerint" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="date">Dátum szerint</SelectItem>
                  <SelectItem value="name">Név szerint</SelectItem>
                  <SelectItem value="location">Helyszín szerint</SelectItem>
                </SelectContent>
              </Select>

              <Button variant="outline" onClick={toggleSortDirection}>
                {sortDirection === 'asc' ? <ArrowUpAZ className="h-4 w-4" /> : <ArrowDownAZ className="h-4 w-4" />}
              </Button>
            </>
          ) : (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline">
                  <ArrowUpAZ className="h-4 w-4 mr-2" />
                  Rendezés
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => setSortBy('date')}>
                  Dátum szerint {sortBy === 'date' && '✓'}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setSortBy('name')}>
                  Név szerint {sortBy === 'name' && '✓'}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setSortBy('location')}>
                  Helyszín szerint {sortBy === 'location' && '✓'}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={toggleSortDirection}>
                  {sortDirection === 'asc' ? 'Növekvő ↑' : 'Csökkenő ↓'}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>

      {filteredEvents.length === 0 && (
        <div className="text-center py-8">
          <CalendarIcon className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium">Nincsenek események</h3>
          <p className="text-muted-foreground mt-2">Próbáld meg módosítani a keresési feltételeket vagy a szűrőket</p>
        </div>
      )}

      {sortedCurrentlyHappeningEvents.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <h2 className="text-xl font-semibold pt-2 text-accent">                  Jelenleg zajlik
            </h2>
            <Badge className="bg-accent/20 text-accent border-none">
              {sortedCurrentlyHappeningEvents.length}
            </Badge>
          </div>
          <div className="space-y-4">
            {sortedCurrentlyHappeningEvents.map(renderEventCard)}
          </div>
        </div>
      )}

      {sortedUpcomingEvents.length > 0 && (
        <>
          {sortedCurrentlyHappeningEvents.length > 0 && (
            <Separator className="my-6" />
          )}
          <div className="space-y-4">
            <h2 className="text-xl font-semibold pt-2">Közelgő események</h2>
            <div className="space-y-4">
              {sortedUpcomingEvents.map(renderEventCard)}
            </div>
          </div>
        </>
      )}

      {sortedPastEvents.length > 0 && (
        <>
          {(sortedUpcomingEvents.length > 0 || sortedCurrentlyHappeningEvents.length > 0) && (
            <Separator className="my-6" />
          )}
          <div className="space-y-4">
            <h2 className="text-xl font-semibold pt-2 text-muted-foreground">Lezajlott rendezvények</h2>
            <div className="space-y-4">
              {sortedPastEvents.map(renderEventCard)}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default EventListView;
